import * as React from 'react';

interface Props {
    reservationId: string;
    personId: string;
    personName: string;
    onCreated: () => void;
    onClose: () => void;
}

export const AdHocVoucherModal: React.FC<Props> = ({ reservationId, personId, personName, onCreated, onClose }) => {
    const [type, setType] = React.useState<string>('Red');
    const [qty, setQty] = React.useState(1);
    const [followStart, setFollowStart] = React.useState(true);
    const [startDate, setStartDate] = React.useState('');
    const [followEnd, setFollowEnd] = React.useState(true);
    const [endDate, setEndDate] = React.useState('');
    const [ptFollowStart, setPtFollowStart] = React.useState(true);
    const [ptStartDate, setPtStartDate] = React.useState('');
    const [ptFollowEnd, setPtFollowEnd] = React.useState(true);
    const [ptEndDate, setPtEndDate] = React.useState('');
    const [notes, setNotes] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);

    const handleSubmit = async () => {
        if (!followStart && !startDate) { alert('Please set a validity start date.'); return; }
        if (!followEnd && !endDate) { alert('Please set a validity end date.'); return; }
        if (type === 'Red' && !ptFollowStart && !ptStartDate) { alert('Please set a PT start date.'); return; }
        if (type === 'Red' && !ptFollowEnd && !ptEndDate) { alert('Please set a PT end date.'); return; }

        setSubmitting(true);
        try {
            const response = await fetch('/api/sales/guest-vouchers/create-adhoc', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reservationId, personId, voucherType: type, quantity: qty,
                    followProgramStart: followStart,
                    validityStart: followStart ? null : new Date(startDate).toISOString(),
                    followProgramEnd: followEnd,
                    validityEnd: followEnd ? null : new Date(endDate).toISOString(),
                    ptFollowProgramStart: ptFollowStart,
                    ptStart: (type === 'Red' && !ptFollowStart) ? new Date(ptStartDate).toISOString() : null,
                    ptFollowProgramEnd: ptFollowEnd,
                    ptEnd: (type === 'Red' && !ptFollowEnd) ? new Date(ptEndDate).toISOString() : null,
                    notes: notes || null,
                }),
            });
            if (!response.ok) {
                const result = await response.json();
                alert(`Failed: ${result.error || 'Unknown error'}`);
            } else {
                onCreated();
            }
        } catch (e) {
            console.error('Create adhoc failed', e);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="gv-submodal-backdrop" onClick={onClose}>
            <div className="gv-submodal" onClick={e => e.stopPropagation()}>
                <div className="gv-submodal-header">
                    <h3>Add Ad-hoc Voucher</h3>
                    <button className="gv-modal-close" onClick={onClose}>&times;</button>
                </div>
                <div className="gv-submodal-body">
                    <div className="gv-submodal-guest">
                        <i className="fa-solid fa-user"></i>
                        {personName}
                    </div>

                    {/* Type & Quantity */}
                    <div className="gv-form-row">
                        <div className="gv-form-group">
                            <label className="gv-form-label">Type</label>
                            <select className="form-select form-select-sm" value={type} onChange={e => {
                                setType(e.target.value);
                                if (e.target.value === 'Blue') { setPtFollowStart(true); setPtStartDate(''); setPtFollowEnd(true); setPtEndDate(''); }
                            }}>
                                <option value="Red">Red</option>
                                <option value="Blue">Blue</option>
                            </select>
                        </div>
                        <div className="gv-form-group">
                            <label className="gv-form-label">Quantity</label>
                            <input type="number" min="1" max="50" value={qty} onChange={e => setQty(parseInt(e.target.value) || 1)}
                                className="form-control form-control-sm" style={{ width: '80px' }} />
                        </div>
                    </div>

                    {/* Validity Period */}
                    <div className="gv-form-section">
                        <label className="gv-form-label">Validity Period</label>
                        <div className="gv-date-range">
                            <div className="gv-date-field">
                                <span className="gv-date-label">From</span>
                                <label className="gv-check-label">
                                    <input type="checkbox" checked={followStart} onChange={e => { setFollowStart(e.target.checked); if (e.target.checked) setStartDate(''); }} />
                                    Follow program
                                </label>
                                {!followStart && (
                                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="form-control form-control-sm" />
                                )}
                            </div>
                            <div className="gv-date-field">
                                <span className="gv-date-label">To</span>
                                <label className="gv-check-label">
                                    <input type="checkbox" checked={followEnd} onChange={e => { setFollowEnd(e.target.checked); if (e.target.checked) setEndDate(''); }} />
                                    Follow program
                                </label>
                                {!followEnd && (
                                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="form-control form-control-sm" />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* PT Validity (Red only) */}
                    {type === 'Red' && (
                        <div className="gv-form-section">
                            <label className="gv-form-label">PT Validity Window</label>
                            <div className="gv-date-range">
                                <div className="gv-date-field">
                                    <span className="gv-date-label">From</span>
                                    <label className="gv-check-label">
                                        <input type="checkbox" checked={ptFollowStart} onChange={e => { setPtFollowStart(e.target.checked); if (e.target.checked) setPtStartDate(''); }} />
                                        Follow program
                                    </label>
                                    {!ptFollowStart && (
                                        <input type="date" value={ptStartDate} onChange={e => setPtStartDate(e.target.value)} className="form-control form-control-sm" />
                                    )}
                                </div>
                                <div className="gv-date-field">
                                    <span className="gv-date-label">To</span>
                                    <label className="gv-check-label">
                                        <input type="checkbox" checked={ptFollowEnd} onChange={e => { setPtFollowEnd(e.target.checked); if (e.target.checked) setPtEndDate(''); }} />
                                        Follow program
                                    </label>
                                    {!ptFollowEnd && (
                                        <input type="date" value={ptEndDate} onChange={e => setPtEndDate(e.target.value)} className="form-control form-control-sm" />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Notes */}
                    <div className="gv-form-section">
                        <label className="gv-form-label">Reason / Notes</label>
                        <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className="form-control form-control-sm" placeholder="Optional..." />
                    </div>
                </div>

                <div className="gv-submodal-footer">
                    <button className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
                    <button className="btn btn-sm btn-success" disabled={submitting} onClick={handleSubmit}>
                        {submitting ? (
                            <><span className="spinner-border spinner-border-sm me-1" />Creating...</>
                        ) : (
                            <><i className="fa-solid fa-plus me-1"></i>Create Voucher{qty > 1 ? 's' : ''}</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
